import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { useNavigate } from 'react-router-dom';
import { getSecureToken } from '@/utils/tokenEncryption';
import { useApp } from '@/store/AppContext';

type Choice = {
  value: string;
  label: string;
};

const userCounts: Choice[] = [
  { value: '0-100', label: '0-100' },
  { value: '100-1000', label: '100-1,000' },
  { value: '1000-10000', label: '1,000-10,000' },
  { value: '10000+', label: '10,000+' }
];

const languages: Choice[] = [
  { value: 'en-US', label: 'English' },
  { value: 'pl-PL', label: 'Polski' }
];

const sanitizeInput = (input: string, maxLength = 100) =>
  String(input || '')
    .replace(/[<>{}]/g, '')
    .substring(0, maxLength);

const steps = [
  { title: 'Personal Information', description: 'Tell us your name' },
  { title: 'Workspace Setup', description: "Let's set up your workspace" },
  { title: 'Language Preference', description: 'Choose your preferred language' },
  { title: 'User Base', description: 'How many monthly users do you have?' }
];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getOnboardingApiBases = () => {
  const hostname = window.location.hostname;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  return isLocalhost
    ? ['/workers/api', window.location.origin + '/api', 'http://localhost:3002/api', 'http://localhost:3001/api']
    : ['https://api.paqshot.com/workers/api', 'https://api.prosecaptain.com/workers/api'];
};

function ChoiceButton({
  label,
  selected,
  onClick
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-xl border px-4 py-3 text-left transition-all ${
        selected
          ? 'border-accent bg-accent/10 text-text-primary'
          : 'border-border bg-bg-control text-text-secondary hover:bg-bg-control-hover'
      }`}
    >
      {label}
    </button>
  );
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { loadMe } = useApp();

  const [step, setStep] = useState(0);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [language, setLanguage] = useState('en-US');
  const [userCount, setUserCount] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleComplete = async () => {
    try {
      setIsSubmitting(true);
      setError('');

      const token = getSecureToken('auth_token');
      if (!token) throw new Error('User not authenticated');

      const payload = {
        firstName,
        lastName,
        workspaceName,
        organizationName: workspaceName,
        language,
        userCount,
        // Legacy fields kept for backward compatibility with older onboarding API validation.
        teamSize: userCount || 'not_specified',
        position: 'not_specified',
        goals: ['not_specified'],
        selectedGoals: ['not_specified']
      };

      let response: Response | null = null;
      let lastNetworkError: unknown = null;
      const bases = getOnboardingApiBases();

      for (const base of bases) {
        let shouldTryNextBase = false;
        for (let attempt = 0; attempt < 3; attempt += 1) {
          try {
            response = await fetch(`${base}/onboarding/complete`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(payload)
            });
            if (response.status === 404) {
              shouldTryNextBase = true;
              response = null;
              break;
            }
            if (response.status !== 429 || attempt === 2) break;
            await sleep(800 * (attempt + 1));
          } catch (error) {
            lastNetworkError = error;
            response = null;
            shouldTryNextBase = true;
            break;
          }
        }
        if (shouldTryNextBase) continue;
        if (response) break;
      }

      if (!response) {
        if (lastNetworkError instanceof Error) {
          throw lastNetworkError;
        }
        throw new Error('Failed to complete onboarding');
      }

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) {
        if (response.status === 429) {
          throw new Error('Server is busy. Please wait a few seconds and try again.');
        }
        throw new Error(data?.error || data?.message || 'Failed to complete onboarding');
      }

      await loadMe();
      navigate('/home', { replace: true });
    } catch (err: any) {
      setError(err?.message || 'Failed to complete onboarding');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    setError('');
    if (step < steps.length - 1) {
      setStep((prev) => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (step > 0) {
      setStep((prev) => prev - 1);
      setError('');
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-4">
            <div>
              <label htmlFor="firstName" className="mb-2 block text-sm font-medium text-text-primary">
                First Name
              </label>
              <input
                id="firstName"
                type="text"
                className="w-full h-11 px-4 bg-bg-control border border-border rounded-lg text-text-primary outline-none transition-all focus:border-accent focus:bg-bg-control-hover"
                placeholder="John"
                value={firstName}
                onChange={(e) => setFirstName(sanitizeInput(e.target.value, 50))}
                maxLength={50}
              />
            </div>
            <div>
              <label htmlFor="lastName" className="mb-2 block text-sm font-medium text-text-primary">
                Last Name
              </label>
              <input
                id="lastName"
                type="text"
                className="w-full h-11 px-4 bg-bg-control border border-border rounded-lg text-text-primary outline-none transition-all focus:border-accent focus:bg-bg-control-hover"
                placeholder="Smith"
                value={lastName}
                onChange={(e) => setLastName(sanitizeInput(e.target.value, 50))}
                maxLength={50}
              />
            </div>
          </div>
        );
      case 1:
        return (
          <div className="space-y-4">
            <input
              id="workspaceName"
              type="text"
              className="w-full h-11 px-4 bg-bg-control border border-border rounded-lg text-text-primary outline-none transition-all focus:border-accent focus:bg-bg-control-hover"
              placeholder="My Workspace"
              value={workspaceName}
              onChange={(e) => setWorkspaceName(sanitizeInput(e.target.value, 100))}
              maxLength={100}
            />
          </div>
        );
      case 2:
        return (
          <div className="space-y-3">
            {languages.map((lang) => (
              <ChoiceButton
                key={lang.value}
                selected={language === lang.value}
                onClick={() => setLanguage(lang.value)}
                label={lang.label}
              />
            ))}
          </div>
        );
      case 3:
        return (
          <div className="space-y-3">
            {userCounts.map((count) => (
              <ChoiceButton
                key={count.value}
                selected={userCount === count.value}
                onClick={() => setUserCount(count.value)}
                label={count.label}
              />
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed z-50 inset-0 bg-bg-main flex flex-col justify-center items-center p-4 md:p-8">
      <div className="w-full max-w-4xl bg-bg-panel py-8 px-6 rounded-xl border border-border mb-16 relative">
        <div className="mb-8 absolute top-0 w-full left-0">
          <div className="overflow-hidden rounded-full bg-bg-control">
            <div
              className="h-2 rounded-full bg-accent transition-all duration-500 ease-in-out"
              style={{ width: `${((step + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>

        <div className="px-4 py-16 sm:p-6">
          <h2 className="text-center text-3xl font-semibold text-text-primary">{steps[step].title}</h2>
          <p className="text-center text-lg mt-2 text-text-secondary mb-8">{steps[step].description}</p>

          {renderStep()}
          {error && <p className="mt-3 text-sm text-red-500">{error}</p>}

          <div className="mt-8 flex justify-between">
            <button
              onClick={handlePrev}
              disabled={step === 0 || isSubmitting}
              className={`inline-flex items-center rounded-lg border px-4 h-10 text-sm ${
                step === 0 || isSubmitting
                  ? 'border-border text-text-disabled bg-bg-control cursor-not-allowed'
                  : 'border-border text-text-primary bg-bg-control hover:bg-bg-control-hover'
              }`}
            >
              <ChevronLeftIcon className="mr-2 h-5 w-5" aria-hidden="true" />
              Previous
            </button>

            <button
              onClick={handleNext}
              disabled={isSubmitting}
              className="inline-flex items-center rounded-lg bg-accent hover:bg-accent-hover text-bg-main px-4 h-10 text-sm font-semibold disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {step < steps.length - 1 ? (
                <>
                  Next
                  <ChevronRightIcon className="ml-2 h-5 w-5" aria-hidden="true" />
                </>
              ) : (
                <>
                  {isSubmitting ? 'Setting up...' : 'Complete'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
